import { convertPrice, roundDisplayedRate, shouldRoundFixedPlanRate } from "./constructor_pricing.js";

/**
 * Pure price-calculation logic — no DOM access.
 *
 * Accepts configuration (exchange rate, currency symbols) and a list of
 * line-items; returns computed rows + total in the requested currency.
 */
export class PriceCalculator {
  constructor({ eurToPlnRate, eurToUsdRate, symbols }) {
    this.eurToPlnRate = eurToPlnRate;
    this.eurToUsdRate = eurToUsdRate || 1.0;
    this.symbols = symbols;
  }

  calculate(currency, items, { projectType, projectCategoryKey } = {}) {
    const rows = items
      .map(({ label, uahPerSqm, eurPerSqm, area, quantity = 1 }) => {
        if (area <= 0 || quantity <= 0) return null;

        const rate = convertPrice({ uah: uahPerSqm, eur: eurPerSqm }, currency, {
          eurToPlnRate: this.eurToPlnRate,
          eurToUsdRate: this.eurToUsdRate,
        });
        const shouldRoundRate =
          projectType && projectType !== "constructor" && shouldRoundFixedPlanRate(projectCategoryKey, currency);
        const displayRate = shouldRoundRate ? roundDisplayedRate(rate, currency) : rate;

        return {
          label,
          area,
          quantity,
          unitRate: displayRate,
          amount: PriceCalculator.roundTo(displayRate * area * quantity, 2),
        };
      })
      .filter(Boolean);

    return {
      rows,
      total: PriceCalculator.roundTo(rows.reduce((sum, row) => sum + row.amount, 0), 2),
      symbol: this.symbols[currency] || "€",
    };
  }

  static roundTo(value, digits = 2) {
    const factor = 10 ** digits;
    return Math.round((Number(value) + Number.EPSILON) * factor) / factor;
  }

  static formatNumber(value, { minimumFractionDigits = 0, maximumFractionDigits = 2 } = {}) {
    return Number(value).toLocaleString("uk-UA", {
      minimumFractionDigits,
      maximumFractionDigits,
    });
  }

  static formatAmount(value) {
    return PriceCalculator.formatNumber(value, { maximumFractionDigits: 2 });
  }

  static formatRate(value) {
    return PriceCalculator.formatNumber(value, { maximumFractionDigits: 3 });
  }
}
